/**
 * Label extent estimation for viewBox / bounds — matches renderer heuristics
 * (displaySymbol, hydrogen offset, paper carbons) without DOM measurement.
 */

import { getOffsetX, calculateGroupOffset } from '@alchemiesolns/line-angle-util';
import { getFormat } from './utils/formatContext';

/**
 * Estimate the visual width of a character in PTSans-Bold (see hitbox / rendering).
 */
export function estimateCharWidth(char: string, fontSize: number): number {
  const widthRatios: Record<string, number> = {
    I: 0.3,
    i: 0.3,
    l: 0.3,
    '1': 0.5,
    C: 0.65,
    H: 0.65,
    N: 0.65,
    O: 0.65,
    S: 0.6,
    P: 0.6,
    F: 0.55,
    B: 0.65,
    r: 0.45,
    t: 0.4,
    '2': 0.55,
    '3': 0.55,
    M: 0.75,
    W: 0.8,
  };
  const ratio = widthRatios[char] ?? 0.6;
  return fontSize * ratio;
}

export function estimateDisplaySymbolWidth(displaySymbol: string, fontSize: number): number {
  let w = 0;
  for (let i = 0; i < displaySymbol.length; i++) {
    w += estimateCharWidth(displaySymbol[i], fontSize);
  }
  return w;
}

/** Minimal recipe fields for label bounds (same shape as `AtomRecipe` in renderAtomFromRecipe). */
export type AtomLabelBoundsRecipe = {
  type: 'text' | 'carbon';
  displaySymbol: string;
  isGroup?: boolean;
  bondDirection?: 'left' | 'right' | 'default';
};

/**
 * Axis-aligned bounds of the rendered label (chemistry coords, y-up) for one atom recipe.
 */
export function getRecipeLabelRectChem(
  recipe: AtomLabelBoundsRecipe,
  atomX: number,
  atomY: number,
  fontSize: number
): { minX: number; maxX: number; minY: number; maxY: number } {
  const format = getFormat();
  if (recipe.type === 'carbon') {
    const r = format.atom.radius + 0.4;
    return { minX: atomX - r, maxX: atomX + r, minY: atomY - r, maxY: atomY + r };
  }

  let hydrogenOffset = 0;
  if (recipe.isGroup && recipe.bondDirection) {
    hydrogenOffset = calculateGroupOffset(recipe.displaySymbol, recipe.bondDirection, fontSize);
  } else {
    hydrogenOffset = getOffsetX(recipe.displaySymbol, fontSize);
  }

  const cx = atomX + format.atom.symbolOffset[0] + hydrogenOffset;
  const cy = atomY + format.atom.symbolOffset[1];
  const halfW = estimateDisplaySymbolWidth(recipe.displaySymbol, fontSize) / 2;
  const halfH = fontSize * 0.6;

  return {
    minX: cx - halfW,
    maxX: cx + halfW,
    minY: cy - halfH,
    maxY: cy + halfH,
  };
}
